/**
 * /me fetcher — typed client for the GET /me session endpoint.
 *
 * Contract (from A1 backend):
 *   - 200 → returns CurrentUser JSON
 *   - 401 → unauthenticated; returns null (not an error)
 *   - other non-ok → throws
 *   - network failure → throws
 *
 * Always sends `credentials: 'include'` so the session cookie rides along.
 */

/**
 * Cohort gate result for the Weekly Briefing feature.
 *
 * Computed per-household from `(today - household_oldest_transaction_date) >= 120 days`
 * — NOT from `users.created_at`. See plan §13 Q4 + Q25.
 *
 * `null` when the user has zero household transactions (e.g. accountless,
 * pre-Plaid-Link). The frontend treats `null` the same as `'lt_4mo'` for
 * gating purposes (no briefing surface shown).
 */
export type BriefingCohort = 'lt_4mo' | 'ge_4mo' | null;

export interface FeatureFlags {
  /**
   * §H3 — Plan §E4.5 backend gate for the entire edit-transaction surface
   * (PATCH /transactions, POST /categorization-rules, etc.) plus the iOS/web
   * UI affordances that drive it. Defaults to false in defensive fallback
   * so older backend responses missing `feature_flags` don't enable a UI
   * the server will reject.
   */
  user_categorization: boolean;
  /**
   * DEC-11 P5 — pilot email allowlist: insights append into user Ask threads
   * and the Deca Insights pin is hidden. Fail-closed default `false`.
   */
  insights_to_user_thread: boolean;
}

export interface CurrentUser {
  id: string;
  email: string;
  name: string | null;
  picture_url: string | null;
  role: 'user' | 'admin';
  csrf_token: string;
  /**
   * IANA timezone identifier (e.g. `'America/Los_Angeles'`). Always populated:
   * defaults to `'UTC'` server-side for users who haven't pushed their
   * detected timezone via PATCH `/api/v1/users/me/preferences` yet.
   *
   * Weekly Briefing (M0): app-shell bootstrap compares this to
   * `Intl.DateTimeFormat().resolvedOptions().timeZone` and PATCHes when they
   * differ (PATCH-on-mismatch). See plan §11.1 + (wwww).
   */
  timezone: string;
  /**
   * Weekly Briefing cohort gate. `null` for accountless users.
   *
   * See plan §13 Q4 + Q25.
   */
  cohort: BriefingCohort;
  /**
   * Plan §H3b — per-household feature-flag map mirroring the backend
   * `resolveFeatureFlags(householdId)` evaluation. Optional on the wire
   * so /me responses written by an older backend (or cached client copies
   * from before this rollout) still decode without throwing. The fetcher
   * fills a fail-closed default (see `fetchMe`).
   */
  feature_flags?: FeatureFlags;
}

export async function fetchMe(): Promise<CurrentUser | null> {
  const response = await fetch('/me', { credentials: 'include' });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`fetchMe failed: ${response.status} ${response.statusText}`);
  }

  const body = (await response.json()) as CurrentUser;
  // Plan §H3b + DEC-11 P5 defensive default: backend should always send
  // `feature_flags`, but to keep the rollout reversible (and to tolerate any
  // /me caches written before this commit shipped), normalize EVERY shape to
  // a strict boolean map so downstream consumers can trust the contract:
  //   - `feature_flags` missing entirely        -> both flags false
  //   - `feature_flags: {}` (partial payload)    -> both flags false
  //   - flag value null / non-boolean            -> that flag false
  //   - explicit `true`                          -> that flag true
  // The `=== true` comparison makes this fail-closed against every wire
  // anomaly. GPT H3b LOW (rev-1): the previous truthy-only check
  // (`if (!body.feature_flags)`) silently passed `{}` through without
  // filling a boolean, breaking the documented contract.
  const rawFlags = body.feature_flags as
    | { user_categorization?: unknown; insights_to_user_thread?: unknown }
    | undefined;
  body.feature_flags = {
    user_categorization: rawFlags?.user_categorization === true,
    insights_to_user_thread: rawFlags?.insights_to_user_thread === true,
  };
  return body;
}
